/**
 * Helper functions for multi-client route validation and navigation.
 * Extracted from routes.js to enable unit testing.
 */

import { EXTERNAL_ROLES } from "../../utils/Roles";
import userHasAccess from "../../utils/userHasAccess";

/**
 * Parses the associatedClientIds from localStorage.
 * Returns an array of integer client IDs, or an empty array if none are stored.
 */
export function getAssociatedClientIdsFromStorage() {
  try {
    return JSON.parse(sessionStorage.getItem("associatedClientIds") || "[]");
  } catch (e) {
    return [];
  }
}

/**
 * Returns the default dashboard index path based on the user's role and associated clients.
 * External users (PLAN_ADMINISTRATOR, INFORMATION_AGENT) are redirected to their first client's members page.
 * Internal users are redirected to the follow-ups page.
 */
export function getDashboardIndex() {
  const associatedClientIds = getAssociatedClientIdsFromStorage();
  const firstClientId = associatedClientIds.length > 0 ? associatedClientIds[0] : null;

  // Only redirect to client members page if user is external AND has at least one associated client.
  // When associatedClientIds is empty and user is external, fallback to no-data. If not, fall back to "follow-ups" to avoid routing to "clients/null/members".
  if (userHasAccess(EXTERNAL_ROLES, true)) {
    if (!!firstClientId)
      return `clients/${firstClientId}/members`;

    return "no-data";
  }

  return "follow-ups";
}

/**
 * Validates whether the current user has access to the requested client.
 * If the user has associated clients and the requested client is not in the list,
 * returns a redirect pathname pointing to the first authorized client.
 * Returns null if no redirect is needed.
 *
 * @param {string|number} requestedClientId - The client ID from route params
 * @param {string} currentPathname - The current location pathname
 * @returns {string|null} The redirect pathname, or null if access is valid
 */
export function getClientAccessRedirect(requestedClientId, currentPathname) {
  if (!userHasAccess(EXTERNAL_ROLES, true) && !requestedClientId) {
    return null;
  }
  const associatedClientIds = getAssociatedClientIdsFromStorage();
  if (associatedClientIds.length === 0) return null;
  if (!requestedClientId) {
    return injectClientIdIntoPath(currentPathname, associatedClientIds[0]);
  }

  return null;
}

function injectClientIdIntoPath(path, newClientId) {
  if (path.endsWith("/")) {
    path = path.slice(0, -1);
  }

  const parts = path.split("/").filter(Boolean);

  const clientsIndex = parts.indexOf("clients");

  if (clientsIndex === -1) {
    return "/" + parts.join("/");
  }

  const clientIdIndex = clientsIndex + 1;

  if (parts.length <= clientIdIndex) {
    parts.push(String(newClientId));
  } else {
    parts[clientIdIndex] = String(newClientId);
  }

  return "/" + parts.join("/");
}


// WEBPACK FOOTER //
// ./src/components/app/routeHelpers.js